"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./components/ui/button";
import { OTPVerification } from "./components/OTPVerification";
import { AstroOTPVerification } from "./components/AstroOTPVerification";
import { AstroBulkUpload } from "./components/AstroBulkUpload";
import { AstroAIReview } from "./components/AstroAIReview";

import { AstroFinalSubmission } from "./components/AstroFinalSubmission";
import { WelcomeDashboard } from "./components/WelcomeDashboard";
import { HRDashboard } from "./components/HRDashboard";
import { EmailTemplate } from "./components/EmailTemplate";
import { AutosaveProvider, useAutosaveContext } from "./components/AutosaveManager";
import { ExitIntentModal } from "./components/ExitIntentModal";
import { ResumeOnboarding } from "./components/ResumeOnboarding";
import { AutosaveIndicator, useExitIntent } from "./components/AutosaveIndicator";
import { TermsAndConditions } from "./components/TermsAndConditions";
import { DocumentCollection } from "./components/DocumentCollection";
import { DocumentReview } from "./components/DocumentReview";
import { DocumentCorrection } from "./components/DocumentCorrection";
import { AppointmentLetter } from "./components/AppointmentLetter";
import { ProgressDemo } from "./components/ProgressDemo";
import { TurfAIConnectionTest } from "./components/TurfAIConnectionTest";
import { SmartDocumentCollection } from "./components/SmartDocumentCollection";
import { CompletenessReview } from "./components/CompletenessReview";
import { TurfAIIntegrationTest } from "./components/TurfAIIntegrationTest";
import { LoginScreen } from "./components/LoginScreen";
import { authService } from "./services/turfai/authService";
import "./services/turfai/manualTest"; // Load manual test functions
import { 
  FileText, 
  Upload, 
  PenTool, 
  ArrowRight, 
  CheckCircle,
  Settings,
  BarChart3,
  Rocket,
  Menu,
  Users,
  Mail,
  TestTube,
  Brain,
  User
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

type AppView = "login" | "demo" | "resume" | "otp" | "astro-otp" | "astro-bulk" | "astro-review" | "astro-final" | "welcome" | "terms" | "astro-terms" | "documents" | "review" | "correction" | "appointment" | "progress" | "complete" | "hr-dashboard" | "email-preview" | "turfai-test" | "smart-collection" | "completeness-review" | "turfai-integration";

export type JoinerType = 'intern' | 'consultant' | 'employee';
export type ExperienceLevel = 'fresher' | 'experienced';

function AppContent() {
  const [currentView, setCurrentView] = React.useState<AppView>("login");
  const [joinerType, setJoinerType] = React.useState<JoinerType>('employee');
  const [experienceLevel, setExperienceLevel] = React.useState<ExperienceLevel>('experienced');
  const [currentExecutionId, setCurrentExecutionId] = React.useState<string | null>(null);
  const [, setIsAuthenticated] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState<any>(null);
  const [employeeData] = React.useState({
    employee_id: 'EMP001',
    full_name: 'John Doe',
    department: 'Engineering',
    start_date: '2025-02-01',
    joiner_type: joinerType,
    experience_level: experienceLevel
  });
  
  const { saveProgress, hasSavedProgress } = useAutosaveContext();
  const { showExitIntent, closeExitIntent } = useExitIntent();

  // Check authentication on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        setIsAuthenticated(true);
        // If already authenticated, skip to demo
        if (currentView === "login") {
          setCurrentView("demo");
        }
      }
    };
    checkAuth();
  }, [currentView]);

  // Login success handler
  const handleLoginSuccess = (userInfo: any) => {
    setIsAuthenticated(true);
    setCurrentUser(userInfo.user);
    setCurrentView("demo");
  };

  // Logout handler
  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView("login");
  };

  const handleShowTerms = () => {
    setCurrentView("terms");
  };

  const handleBackToDemo = () => {
    setCurrentView("demo");
  };

  const handleBackToOTP = () => {
    setCurrentView("otp");
  };

  const handleVerificationSuccess = () => {
    setCurrentView("documents");
  };

  const handleAstroVerificationSuccess = async () => {
    await saveCurrentProgress("astro-bulk", 2);
    setCurrentView("astro-bulk");
  };

  const handleAstroBulkUploadComplete = async () => {
    await saveCurrentProgress("astro-review", 3);
    setCurrentView("astro-review");
  };

  const handleAstroReviewComplete = async () => {
    await saveCurrentProgress("astro-final", 4);
    setCurrentView("astro-final");
  };

  const handleFinalSubmissionComplete = async () => {
    await saveCurrentProgress("welcome", 5);
    setCurrentView("welcome");
  };

  const handleWelcomeToDemos = () => {
    setCurrentView("demo");
  };

  const handleDocumentCollectionComplete = () => {
    setCurrentView("review");
  };

  const handleDocumentReviewComplete = () => {
    setCurrentView("complete");
  };

  const handleCorrectionComplete = () => {
    setCurrentView("complete");
  };

  const handleAppointmentComplete = () => {
    setCurrentView("complete");
  };

  const handleBackToDocuments = () => {
    setCurrentView("documents");
  };

  // Demo workflow handlers
  const handleStartDocumentUpload = () => {
    setCurrentView("otp");
  };

  const handleStartCorrection = () => {
    setCurrentView("correction");
  };

  const handleStartAppointmentSigning = () => {
    setCurrentView("appointment");
  };

  // Smart collection handlers
  const handleStartSmartCollection = () => {
    setCurrentView("smart-collection");
  };

  const handleSmartCollectionComplete = (results: any) => {
    // Store execution ID from results
    if (results?.executionId) {
      setCurrentExecutionId(results.executionId);
    }
    setCurrentView("completeness-review");
  };

  const handleCompletenessReviewComplete = () => {
    setCurrentView("complete");
  };

  const handleUploadMoreDocuments = () => {
    setCurrentView("smart-collection");
  };

  const handleStartProgressTracking = () => {
    setCurrentView("progress");
  };

  // New workflow handler
  const handleStartNewWorkflow = () => {
    setCurrentView("astro-terms");
  };

  // Terms acceptance handler for new workflow
  const handleTermsAccepted = () => {
    setCurrentView("astro-otp");
  };

  // HR Dashboard handler
  const handleStartHRDashboard = () => {
    setCurrentView("hr-dashboard");
  };

  // Email preview handler
  const handleEmailPreview = () => {
    setCurrentView("email-preview");
  };

  const handleTurfAITest = () => {
    setCurrentView("turfai-test");
  };

  const handleTurfAIIntegration = () => {
    setCurrentView("turfai-integration");
  };

  // Check for saved progress on app start
  React.useEffect(() => {
    if (hasSavedProgress() && currentView === "demo") {
      setCurrentView("resume");
    }
  }, []);

  // Autosave progress when moving between steps
  const saveCurrentProgress = React.useCallback(async (step: string, index: number) => {
    await saveProgress({
      currentStep: step,
      stepIndex: index,
      totalSteps: 6,
      joinerType,
      experienceLevel,
      completedSteps: Array.from({ length: index }, (_, i) => `step_${i + 1}`)
    });
  }, [saveProgress, joinerType, experienceLevel]);

  // Resume handlers
  const handleResumeProgress = (progress: any) => {
    setJoinerType(progress.joinerType || 'employee');
    setExperienceLevel(progress.experienceLevel || 'experienced');
    setCurrentView(progress.currentStep);
  };

  const handleStartOverFromResume = () => {
    setCurrentView("demo");
  };

  // Exit intent handlers
  const handleExitWithSave = () => {
    // Progress already saved, just close
    closeExitIntent();
    window.close(); // In real app, this would navigate away
  };

  const handleExitWithoutSaving = () => {
    closeExitIntent();
    window.close(); // In real app, this would navigate away
  };

  // Get current step info for exit intent
  const getCurrentStepInfo = () => {
    const stepMap: Record<string, number> = {
      'demo': 0,
      'astro-terms': 1,
      'astro-otp': 2,
      'astro-bulk': 3,
      'astro-review': 4,
      'astro-final': 5,
      'welcome': 6
    };
    
    return {
      step: currentView,
      index: stepMap[currentView] || 0,
      total: 6
    };
  };

  const stepInfo = getCurrentStepInfo();
  const isInWorkflow = ['astro-terms', 'astro-otp', 'astro-bulk', 'astro-review', 'astro-final'].includes(currentView);

  return (
    <>
      {currentView === "login" && (
        <LoginScreen onLoginSuccess={handleLoginSuccess} />
      )}

      {currentView === "resume" && (
        <ResumeOnboarding 
          onContinue={handleResumeProgress}
          onStartOver={handleStartOverFromResume}
          onShowTerms={handleShowTerms}
        />
      )}

      {currentView === "demo" && (
        <DemoScreen 
          onShowTerms={handleShowTerms}
          onStartDocumentUpload={handleStartDocumentUpload}
          onStartCorrection={handleStartCorrection}
          onStartAppointmentSigning={handleStartAppointmentSigning}
          onStartProgressTracking={handleStartProgressTracking}
          onStartNewWorkflow={handleStartNewWorkflow}
          onStartHRDashboard={handleStartHRDashboard}
          onEmailPreview={handleEmailPreview}
          onTurfAITest={handleTurfAITest}
          onStartSmartCollection={handleStartSmartCollection}
          onTurfAIIntegration={handleTurfAIIntegration}
          onLogout={handleLogout}
          currentUser={currentUser}
        />
      )}

      {currentView === "otp" && (
        <OTPVerification 
          onShowTerms={handleShowTerms}
          onVerificationSuccess={handleVerificationSuccess}
        />
      )}

      {currentView === "astro-otp" && (
        <AstroOTPVerification 
          onShowTerms={handleShowTerms}
          onVerificationSuccess={handleAstroVerificationSuccess}
          onBack={() => setCurrentView("astro-terms")}
        />
      )}

      {currentView === "astro-bulk" && (
        <AstroBulkUpload 
          onShowTerms={handleShowTerms}
          onUploadComplete={handleAstroBulkUploadComplete}
          onBack={() => setCurrentView("astro-otp")}
        />
      )}

      {currentView === "astro-review" && (
        <AstroAIReview 
          onShowTerms={handleShowTerms}
          onReviewComplete={handleAstroReviewComplete}
          onBack={() => setCurrentView("astro-bulk")}
        />
      )}

      {currentView === "astro-final" && (
        <AstroFinalSubmission 
          onShowTerms={handleShowTerms}
          onSubmit={handleFinalSubmissionComplete}
          onBack={() => setCurrentView("astro-review")}
          joinerType={joinerType}
          experienceLevel={experienceLevel}
        />
      )}

      {currentView === "welcome" && (
        <WelcomeDashboard 
          onShowTerms={handleShowTerms}
          onLogout={handleWelcomeToDemos}
          joinerType={joinerType}
        />
      )}
      
      {currentView === "terms" && (
        <TermsAndConditions onBack={handleBackToOTP} />
      )}

      {currentView === "astro-terms" && (
        <TermsAndConditions 
          onBack={handleBackToDemo}
          onAccept={handleTermsAccepted}
          onShowPrivacyPolicy={() => {}}
        />
      )}
      
      {currentView === "documents" && (
        <DocumentCollection 
          onComplete={handleDocumentCollectionComplete}
          onShowTerms={handleShowTerms}
        />
      )}

      {currentView === "review" && (
        <DocumentReview 
          onComplete={handleDocumentReviewComplete}
          onShowTerms={handleShowTerms}
          onBack={handleBackToDocuments}
        />
      )}
      
      {currentView === "correction" && (
        <DocumentCorrection
          onComplete={handleCorrectionComplete}
          onShowTerms={handleShowTerms}
          onBack={handleBackToDemo}
        />
      )}

      {currentView === "appointment" && (
        <AppointmentLetter
          onComplete={handleAppointmentComplete}
          onShowTerms={handleShowTerms}
          onBack={handleBackToDemo}
        />
      )}

      {currentView === "progress" && (
        <ProgressDemo
          onBack={handleBackToDemo}
          onShowTerms={handleShowTerms}
        />
      )}
      
      {currentView === "complete" && (
        <CompletionScreen 
          onBackToDemo={handleBackToDemo}
          onShowTerms={handleShowTerms}
        />
      )}

      {currentView === "hr-dashboard" && (
        <HRDashboard 
          onBack={handleBackToDemo}
          onShowTerms={handleShowTerms}
        />
      )}

      {currentView === "turfai-test" && (
        <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
          <TurfAIConnectionTest onBack={() => setCurrentView("demo")} />
        </div>
      )}

      {currentView === "turfai-integration" && (
        <TurfAIIntegrationTest onBack={() => setCurrentView("demo")} />
      )}

      {currentView === "smart-collection" && (
        <SmartDocumentCollection
          employeeData={employeeData}
          onComplete={handleSmartCollectionComplete}
          onShowTerms={handleShowTerms}
        />
      )}

      {currentView === "completeness-review" && currentExecutionId && (
        <CompletenessReview
          executionId={currentExecutionId}
          employeeData={employeeData}
          onUploadMore={handleUploadMoreDocuments}
          onSubmitForApproval={handleCompletenessReviewComplete}
          onRequestCorrections={() => {
            // Could navigate to correction flow or send notification
          }}
        />
      )}
      {currentView === "email-preview" && (
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 text-center">
              <Button
                variant="outline"
                onClick={handleBackToDemo}
                className="mb-4"
              >
                ← Back to Demo
              </Button>
              <h1 className="text-gray-900 mb-2">Smart Notification Emails</h1>
              <p className="text-gray-600">Preview of automated email notifications sent to candidates and HR</p>
            </div>
            
            <div className="space-y-8">
              <div>
                <h2 className="text-gray-900 mb-4">Candidate Incomplete Notification</h2>
                <EmailTemplate 
                  type="candidate_incomplete"
                  candidateName="Sarah"
                  currentStep="Document Review"
                  onPreviewClick={() => {}}
                />
              </div>
              
              <div>
                <h2 className="text-gray-900 mb-4">HR Review Pending Notification</h2>
                <EmailTemplate 
                  type="hr_review_pending"
                  candidateName="Mike"
                  onPreviewClick={() => setCurrentView("hr-dashboard")}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Autosave Indicator - show during workflow */}
      {isInWorkflow && <AutosaveIndicator />}

      {/* Exit Intent Modal */}
      <ExitIntentModal
        isOpen={showExitIntent && isInWorkflow}
        onClose={closeExitIntent}
        onSaveAndExit={handleExitWithSave}
        onExitWithoutSaving={handleExitWithoutSaving}
        currentStep={stepInfo.step}
        stepIndex={stepInfo.index}
        totalSteps={stepInfo.total}
      />
    </>
  );
}

export default function App() {
  return (
    <AutosaveProvider>
      <AppContent />
    </AutosaveProvider>
  );
}

// Demo Screen Component
function DemoScreen({ 
  onShowTerms, 
  onStartDocumentUpload, 
  onStartCorrection, 
  onStartAppointmentSigning,
  onStartProgressTracking,
  onStartNewWorkflow,
  onStartHRDashboard,
  onEmailPreview,
  onTurfAITest,
  onStartSmartCollection,
  onTurfAIIntegration,
  onLogout,
  currentUser
}: { 
  onShowTerms?: () => void;
  onStartDocumentUpload: () => void;
  onStartCorrection: () => void;
  onStartAppointmentSigning: () => void;
  onStartProgressTracking: () => void;
  onStartNewWorkflow: () => void;
  onStartHRDashboard: () => void;
  onEmailPreview: () => void;
  onTurfAITest: () => void;
  onStartSmartCollection: () => void;
  onTurfAIIntegration: () => void;
  onLogout?: () => void;
  currentUser?: any;
}) {
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);

  const workflows = [
    {
      id: "turfai-integration",
      title: "TurfAI Workflow Integration 🚀",
      description: "Test live document workflows with TurfAI - classify documents, extract data, or do both with any document type.",
      icon: Brain,
      color: "from-green-500 to-teal-600",
      steps: ["Upload Document", "Select Workflow", "View Results"],
      onStart: onTurfAIIntegration,
      featured: true,
      available: true
    },
    {
      id: "smart-collection",
      title: "TurfAI Smart Collection 🤖",
      description: "Upload documents and let TurfAI automatically classify, extract, and validate with real-time completeness tracking.",
      icon: Brain,
      color: "from-purple-600 to-blue-600",
      steps: ["Smart Upload", "AI Processing", "Completeness Review", "Submit/Correct"],
      onStart: onStartSmartCollection,
      featured: true,
      available: false,
      comingSoon: true
    },
    {
      id: "new-workflow",
      title: "New Workflow ✨",
      description: "Experience the next-generation onboarding with Astro, your AI companion guiding you through a streamlined journey.",
      icon: Rocket,
      color: "from-purple-500 to-pink-500",
      steps: ["Terms Agreement", "Astro OTP", "Smart Bulk Upload", "AI Review", "Final Submission", "Welcome Dashboard"],
      onStart: onStartNewWorkflow,
      featured: true,
      available: false,
      comingSoon: true
    },
    {
      id: "document-upload",
      title: "Document Upload Workflow",
      description: "Experience the complete onboarding journey from OTP verification to document collection.",
      icon: Upload,
      color: "from-blue-500 to-blue-600",
      steps: ["OTP Verification", "Document Collection", "Review and Submit"],
      onStart: onStartDocumentUpload,
      available: false,
      comingSoon: true
    },
    {
      id: "document-correction",
      title: "Document Correction Workflow", 
      description: "See how candidates handle HR feedback and document resubmission requests.",
      icon: FileText,
      color: "from-orange-500 to-orange-600",
      steps: ["Review Corrections", "Upload Fixed Documents", "Resubmit"],
      onStart: onStartCorrection,
      available: false,
      comingSoon: true
    },
    {
      id: "appointment-signing",
      title: "Appointment Letter Workflow",
      description: "Sign your appointment letter with digital signature or upload functionality.",
      icon: PenTool,
      color: "from-green-500 to-green-600", 
      steps: ["Review Letter", "Add Signature", "Submit"],
      onStart: onStartAppointmentSigning,
      available: false,
      comingSoon: true
    },
    {
      id: "progress-tracking",
      title: "Progress Tracking Workflow",
      description: "Track onboarding progress with interactive stepper and real-time status updates.",
      icon: BarChart3,
      color: "from-purple-500 to-indigo-600",
      steps: ["View Progress", "Interactive Demo", "Different Layouts"],
      onStart: onStartProgressTracking,
      available: false,
      comingSoon: true
    },
    {
      id: "hr-dashboard",
      title: "HR Dashboard & Notifications",
      description: "Experience the HR side with pending reviews, smart notifications, and candidate management.",
      icon: Users,
      color: "from-teal-500 to-teal-600",
      steps: ["Review Submissions", "Smart Notifications", "Candidate Management"],
      onStart: onStartHRDashboard,
      available: false,
      comingSoon: true
    },
    {
      id: "email-notifications",
      title: "Smart Email Notifications",
      description: "Preview automated email templates for candidate nudges and HR alerts with Astro branding.",
      icon: Mail,
      color: "from-pink-500 to-rose-600",
      steps: ["Candidate Nudges", "HR Alerts", "Email Templates"],
      onStart: onEmailPreview,
      available: false,
      comingSoon: true
    },
    {
      id: "turfai-test",
      title: "TurfAI Connection Test",
      description: "Test the connection to TurfAI services including authentication, DMS, and workflow services.",
      icon: TestTube,
      color: "from-cyan-500 to-blue-600",
      steps: ["Auth Test", "DMS Test", "Workflow Test"],
      onStart: onTurfAITest,
      available: false,
      comingSoon: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {currentUser && (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-gray-700">
                  Welcome, {currentUser.username || currentUser.email || 'User'}
                </span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-purple-600" />
              <span className="text-gray-900">Production Ready</span>
            </div>
            {onLogout && (
              <Button
                variant="outline"
                onClick={onLogout}
                className="text-gray-600 hover:text-red-600 border-gray-300 hover:border-red-300"
              >
                Logout
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 p-4 bg-white/80 rounded-lg border border-gray-200/50"
            >
              {currentUser && (
                <div className="flex items-center space-x-3 px-3 py-2 mb-2 bg-purple-50 rounded">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-3 h-3 text-purple-600" />
                  </div>
                  <span className="text-sm text-gray-700">
                    {currentUser.username || currentUser.email || 'User'}
                  </span>
                </div>
              )}
              <div className="flex items-center space-x-2 px-3 py-2 mb-2">
                <Settings className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-gray-900">Production Ready</span>
              </div>
              {onLogout && (
                <Button
                  variant="outline"
                  onClick={onLogout}
                  className="w-full justify-start text-gray-600 hover:text-red-600 border-gray-300 hover:border-red-300"
                >
                  Logout
                </Button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="relative z-10 w-full max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mx-auto flex items-center justify-center mb-8">
              <Settings className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              DayOne Demo Workflows 🚀
            </h1>
            
            <p className="text-gray-600 max-w-3xl mx-auto mb-8">
              Explore our comprehensive candidate onboarding platform. Try each workflow independently 
              to see how DayOne streamlines the entire hiring process from document collection to final signing.
            </p>
          </motion.div>

          {/* Workflow Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workflows.map((workflow, index) => (
              <motion.div
                key={workflow.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className={`relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20 transition-all duration-300 group ${
                  workflow.featured ? 'ring-2 ring-purple-200' : ''
                } ${workflow.available !== false ? 'hover:shadow-xl cursor-pointer' : 'opacity-70'}`}
              >
                {/* Coming Soon Overlay */}
                {workflow.comingSoon && (
                  <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-[1px] rounded-3xl flex items-center justify-center z-10">
                    <div className="text-center">
                      <div className="text-white text-2xl font-bold mb-2">Coming Soon</div>
                      <div className="text-gray-200 text-sm">Feature in development</div>
                    </div>
                  </div>
                )}

                {workflow.featured && workflow.available !== false && (
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs">
                      ✨ Featured & Available
                    </span>
                  </div>
                )}
                
                {workflow.featured && workflow.comingSoon && (
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-3 py-1 rounded-full text-xs">
                      ✨ Coming Soon
                    </span>
                  </div>
                )}
                
                <div className={`w-16 h-16 bg-gradient-to-r ${workflow.color} rounded-2xl flex items-center justify-center mb-6 ${
                  workflow.available !== false ? 'group-hover:scale-110' : ''
                } transition-transform duration-300`}>
                  <workflow.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className={`mb-4 ${workflow.comingSoon ? 'text-gray-600' : 'text-gray-900'}`}>
                  {workflow.title}
                </h3>
                
                <p className={`mb-6 leading-relaxed ${workflow.comingSoon ? 'text-gray-500' : 'text-gray-600'}`}>
                  {workflow.description}
                </p>

                {/* Steps Preview */}
                <div className="mb-6">
                  <h4 className={`text-sm mb-3 ${workflow.comingSoon ? 'text-gray-500' : 'text-gray-700'}`}>
                    Workflow Steps:
                  </h4>
                  <div className="space-y-2">
                    {workflow.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                          workflow.comingSoon ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {stepIndex + 1}
                        </div>
                        <span className={`text-sm ${workflow.comingSoon ? 'text-gray-500' : 'text-gray-600'}`}>
                          {step}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={workflow.available !== false ? workflow.onStart : undefined}
                  disabled={workflow.available === false}
                  className={`w-full text-white transition-opacity ${
                    workflow.available !== false 
                      ? `bg-gradient-to-r ${workflow.color} hover:opacity-90` 
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {workflow.comingSoon ? 'Coming Soon' : 'Try This Workflow'}
                  {workflow.available !== false && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Feature Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-16 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20"
          >
            <h3 className="text-gray-900 mb-6 text-center">Platform Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-gray-900 mb-2">AI-Powered Verification</h4>
                <p className="text-sm text-gray-600">Automated document processing with TurfAI</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="text-gray-900 mb-2">Smart Corrections</h4>
                <p className="text-sm text-gray-600">Guided workflow for document fixes</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <PenTool className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="text-gray-900 mb-2">Digital Signatures</h4>
                <p className="text-sm text-gray-600">Secure electronic signing capabilities</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-6 h-6 text-indigo-600" />
                </div>
                <h4 className="text-gray-900 mb-2">Progress Tracking</h4>
                <p className="text-sm text-gray-600">Real-time onboarding status updates</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Enhanced Completion Screen Component
function CompletionScreen({ 
  onBackToDemo,
  onShowTerms
}: { 
  onBackToDemo: () => void;
  onShowTerms?: () => void;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-green-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-green-100/10 to-transparent rounded-full" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="relative z-10 text-center max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full mx-auto flex items-center justify-center mb-8">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              Workflow Complete! 🎉
            </h1>
            
            <p className="text-gray-600 mb-8">
              You've successfully completed the onboarding workflow. Experience how DayOne streamlines candidate management.
            </p>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 mb-8">
              <h3 className="text-gray-900 mb-4">Ready to explore more?</h3>
              <div className="text-left space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Try our document correction workflow</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Experience the appointment letter signing process</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>See the complete candidate journey with progress tracking</p>
                </div>
              </div>
            </div>

            <Button
              onClick={onBackToDemo}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 mb-4"
            >
              Back to Demo Workflows
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}